import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Ean13Barcode } from '@tailwindComponents/Ean13Barcode';
import { PanelFieldBox, notSet } from '@tailwindComponents/PanelFieldBox';
import {
  DimensionsEditor,
  PanelControlRow,
  PanelImagePicker,
  PanelInput,
  PanelSelect,
  PanelTextarea,
  formatDimensions,
} from './panelControls';
import {
  DIMENSION_UNIT_OPTIONS,
  ProductPanelDraft,
  TEMPERATURE_CONTROL_OPTIONS,
  VOLUME_UNIT_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
  unitLabel,
} from './panelEdit';

export interface ProductDetailsPanelProps {
  draft: ProductPanelDraft;
  onDraftChange: (draft: ProductPanelDraft) => void;
  isEditing: boolean;
  imageSrc: string | null;
  productName: string;
  canManageSkus: boolean;
  onEdit: () => void;
}

export function ProductDetailsPanel({
  draft,
  onDraftChange,
  isEditing,
  imageSrc,
  productName,
  canManageSkus,
  onEdit,
}: ProductDetailsPanelProps) {
  const [isFurtherInformationOpen, setIsFurtherInformationOpen] =
    useState(false);
  const dimensionsText = formatDimensions(draft.dimensions);
  const patch = (change: Partial<ProductPanelDraft>) =>
    onDraftChange({ ...draft, ...change });

  const stageChosenImage = (chosenImageFile: File) => {
    if (!isEditing) onEdit();
    patch({ chosenImageFile });
  };

  const weightAndVolume =
    draft.weight || draft.volume ? (
      <span className='flex items-center gap-2'>
        <span>
          {draft.weight ? `${draft.weight} ${draft.weightUnitOfMeasure}` : '—'}
        </span>
        <span className='text-gray-300'>|</span>
        <span>
          {draft.volume
            ? `${draft.volume} ${unitLabel(draft.volumeUnitOfMeasure)}`
            : '—'}
        </span>
      </span>
    ) : (
      notSet
    );

  return (
    <div
      data-connector-panel
      className='rounded-md border border-gray-300 bg-gray-50/50 p-4'
    >
      <p className='mb-3.5 text-sm font-semibold text-gray-900'>
        Product details (sellable inner unit)
      </p>

      <div className='flex flex-wrap items-stretch gap-3'>
        <PanelImagePicker
          imageSrc={imageSrc}
          chosenImageFile={draft.chosenImageFile}
          imageNoun='product'
          altText={productName}
          isChangeable={isEditing || canManageSkus}
          onImageChosen={stageChosenImage}
        />

        <div className='flex min-w-[130px] flex-1 flex-col gap-2.5'>
          <PanelFieldBox
            isEmphasised
            label='Item ID'
            isEditing={false}
            value={draft.productId || notSet}
            editor={null}
          />
          <PanelFieldBox
            isEmphasised
            label='Name'
            isEditing={isEditing}
            value={draft.name || notSet}
            editor={
              <PanelInput
                ariaLabel='Product name'
                value={draft.name}
                onChange={(name) => patch({ name })}
              />
            }
          />
        </div>

        <div className='flex min-w-[175px] flex-1 flex-col rounded-lg border border-gray-200 bg-white px-3.5 py-2.5'>
          <p className='mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
            Barcode
            <span className='ml-1 font-medium text-gray-300'>(optional)</span>
          </p>
          {isEditing ? (
            <PanelInput
              ariaLabel='Product barcode'
              maxLength={13}
              placeholder='12 - 13 digits'
              value={draft.barcode}
              onChange={(barcode) => patch({ barcode })}
            />
          ) : (
            <p className='text-base font-semibold tracking-wider text-gray-900'>
              {draft.barcode || notSet}
            </p>
          )}
          <Ean13Barcode barcode={draft.barcode} className='mt-auto block' />
          {!draft.barcode && (
            <p className='mt-2 text-xs text-gray-400'>No barcode yet</p>
          )}
        </div>

        <div className='flex min-w-[270px] flex-[2] flex-col gap-2.5'>
          <PanelFieldBox
            isEmphasised
            label='Dimensions (L × W × H)'
            isEditing={isEditing}
            value={dimensionsText ?? notSet}
            editor={
              <DimensionsEditor
                label='Product dimensions'
                unitOptions={DIMENSION_UNIT_OPTIONS}
                value={draft.dimensions}
                onChange={(dimensions) => patch({ dimensions })}
              />
            }
          />
          <PanelFieldBox
            isEmphasised
            label='Weight & volume'
            isEditing={isEditing}
            value={weightAndVolume}
            editor={
              <PanelControlRow>
                <PanelInput
                  isCompact
                  ariaLabel='Product weight'
                  value={draft.weight}
                  onChange={(weight) => patch({ weight })}
                />
                <PanelSelect
                  isAuto
                  ariaLabel='Product weight unit'
                  value={draft.weightUnitOfMeasure}
                  options={WEIGHT_UNIT_OPTIONS}
                  onChange={(weightUnitOfMeasure) =>
                    patch({ weightUnitOfMeasure })
                  }
                />
                <span className='text-gray-300'>|</span>
                <PanelInput
                  isCompact
                  ariaLabel='Product volume'
                  value={draft.volume}
                  onChange={(volume) => patch({ volume })}
                />
                <PanelSelect
                  isAuto
                  ariaLabel='Product volume unit'
                  value={draft.volumeUnitOfMeasure}
                  options={VOLUME_UNIT_OPTIONS}
                  formatOption={unitLabel}
                  onChange={(volumeUnitOfMeasure) =>
                    patch({ volumeUnitOfMeasure })
                  }
                />
              </PanelControlRow>
            }
          />
        </div>

        <div className='flex min-w-[150px] shrink-0 basis-[150px] flex-col gap-2.5'>
          <PanelFieldBox
            isEmphasised
            label='Temp. Control'
            isEditing={isEditing}
            value={draft.temperatureControlType || notSet}
            editor={
              <PanelSelect
                ariaLabel='Temperature control'
                value={draft.temperatureControlType}
                options={TEMPERATURE_CONTROL_OPTIONS}
                formatOption={(option) => option || 'Not set'}
                onChange={(temperatureControlType) =>
                  patch({ temperatureControlType })
                }
              />
            }
          />
        </div>
      </div>

      <div className='mt-3.5'>
        <button
          type='button'
          className='inline-flex items-center gap-2 text-sm font-semibold text-gray-700'
          aria-expanded={isFurtherInformationOpen}
          onClick={() => setIsFurtherInformationOpen((isOpen) => !isOpen)}
        >
          Further information
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-500 transition-transform ${
              isFurtherInformationOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
        {isFurtherInformationOpen && (
          <div className='mt-2.5 grid grid-cols-3 gap-3'>
            <PanelFieldBox
              isOptional
              className='col-span-3'
              label='Description'
              isEditing={isEditing}
              value={draft.description || notSet}
              editor={
                <PanelTextarea
                  ariaLabel='Product description'
                  value={draft.description}
                  onChange={(description) => patch({ description })}
                />
              }
            />
            <PanelFieldBox
              isOptional
              label='Supplier'
              isEditing={isEditing}
              value={draft.supplier || notSet}
              editor={
                <PanelInput
                  ariaLabel='Product supplier'
                  value={draft.supplier}
                  onChange={(supplier) => patch({ supplier })}
                />
              }
            />
            <PanelFieldBox
              isOptional
              label='Brand'
              isEditing={isEditing}
              value={draft.brand || notSet}
              editor={
                <PanelInput
                  ariaLabel='Product brand'
                  value={draft.brand}
                  onChange={(brand) => patch({ brand })}
                />
              }
            />
            <PanelFieldBox
              isOptional
              label='Category'
              isEditing={isEditing}
              value={draft.category || notSet}
              editor={
                <PanelInput
                  ariaLabel='Product category'
                  value={draft.category}
                  onChange={(category) => patch({ category })}
                />
              }
            />
            <PanelFieldBox
              isOptional
              label='Subcategory'
              isEditing={isEditing}
              value={draft.subCategory || notSet}
              editor={
                <PanelInput
                  ariaLabel='Product subcategory'
                  value={draft.subCategory}
                  onChange={(subCategory) => patch({ subCategory })}
                />
              }
            />
          </div>
        )}
      </div>

      {canManageSkus && !isEditing && (
        <button
          type='button'
          className='mt-3 text-sm font-medium text-primary hover:underline'
          onClick={onEdit}
        >
          Edit product details
        </button>
      )}
    </div>
  );
}
